import { Link } from "react-router-dom";
import { EmptyState } from "../../components/EmptyState";
import { StatusBadge } from "../../components/StatusBadge";
import { formatDateTime, formatNumber } from "../../lib/format";
import { auditLogs, homeExceptions, homeWorkItems, lastOrderPullAt, onlineMismatchCount, productRows } from "../../lib/selectors";
import type { AnyRecord, AppData, WorkItem } from "../../lib/types";

export function HomePage({ data }: { data: AppData }) {
  const workItems = homeWorkItems(data);
  const exceptions = homeExceptions(data);
  const logs = auditLogs(data).slice(0, 8).map(activityRow);
  return (
    <div className="page-stack">
      <section className="two-column">
        <WorkQueue title="오늘 할 일" body="오늘 바로 처리할 작업" items={workItems} />
        <WorkQueue title="예외" body="실수와 과판매를 막는 확인 항목" items={exceptions} />
      </section>
      <OnlineMallSync data={data} />
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>빠른 작업</h2>
            <p>외부 채널에 바로 쓰지 않고 안전한 입력 화면으로 이동합니다.</p>
          </div>
        </div>
        <div className="quick-actions">
          <Link to="/products">상품 등록<span>상품 기본 정보와 옵션을 등록</span></Link>
          <Link to="/content">콘텐츠 확인<span>Archive와 룩북 콘텐츠</span></Link>
          <Link to="/receiving">입고 등록<span>오프라인 위치로 바로 입고</span></Link>
          <Link to="/mobile/barcode">바코드 작업<span>모바일 카메라로 입고/이동/출고</span></Link>
          <Link to="/stores">매장 운영<span>매장 재고와 오프라인 판매 차감</span></Link>
          <Link to="/inventory">재고 조정<span>사유와 로그를 남기는 수량 조정</span></Link>
          <Link to="/shipping">출고 처리<span>피킹/패킹/출고 확정</span></Link>
        </div>
      </section>
      <section className="panel">
        <div className="panel-head">
          <div>
            <h2>최근 활동</h2>
            <p>누가 무엇을 바꿨는지 빠르게 확인합니다.</p>
          </div>
        </div>
        <div className="activity-list">
          {logs.length ? logs.map((log) => (
            <div className="activity-row" key={log.key}>
              <time>{formatDateTime(log.createdAt)}</time>
              <strong>{log.actor}</strong>
              <span>{log.summary}</span>
            </div>
          )) : <EmptyState title="최근 활동이 없습니다." body="상품, 재고, 주문 작업이 기록되면 이곳에 표시됩니다." />}
        </div>
      </section>
    </div>
  );
}

function OnlineMallSync({ data }: { data: AppData }) {
  const providers = onlineMallProviders(data);
  const cafe24 = providers.find((provider) => provider.provider === "cafe24") || providers[0];
  const mappedProducts = productRows(data).filter((product) => product.channelStatus !== "매핑 필요").length;
  const unmappedProducts = productRows(data).filter((product) => product.channelStatus === "매핑 필요").length;
  const mismatch = onlineMismatchCount(data);
  const ordersPulledAt = lastOrderPullAt(data);
  const rows = [
    {
      key: "connection",
      title: "온라인몰 연결",
      body: cafe24 ? `${cafe24.label || "Cafe24"} ${isProviderConnected(cafe24) ? "연결됨" : "확인 필요"}` : "등록된 온라인몰 없음",
      value: cafe24 ? (isProviderConnected(cafe24) ? "정상" : "확인 필요") : "없음",
      tone: cafe24 && isProviderConnected(cafe24) ? "success" : "warning",
      target: "/integrations"
    },
    {
      key: "orders",
      title: "주문 읽기",
      body: ordersPulledAt ? `최근 읽기 ${formatDateTime(ordersPulledAt)}` : "아직 읽은 주문 없음",
      value: ordersPulledAt ? "동기화됨" : "대기",
      tone: ordersPulledAt ? "success" : "default",
      target: "/orders"
    },
    {
      key: "products",
      title: "상품 매핑",
      body: `연결 ${formatNumber(mappedProducts)}개 · 미연결 ${formatNumber(unmappedProducts)}개`,
      value: unmappedProducts ? "확인 필요" : "정상",
      tone: unmappedProducts ? "warning" : "success",
      target: unmappedProducts ? "/products?view=unmapped" : "/products"
    },
    {
      key: "inventory",
      title: "재고 차이",
      body: `온라인몰 수량 차이 ${formatNumber(mismatch)}건`,
      value: mismatch ? "확인 필요" : "정상",
      tone: mismatch ? "warning" : "success",
      target: mismatch ? "/inventory?view=online_mismatch" : "/inventory"
    }
  ] as const;

  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>온라인몰 동기화</h2>
          <p>연결 상태와 주문, 상품, 재고 반영 상태를 Home에서 확인합니다.</p>
        </div>
      </div>
      <div className="sync-list">
        {rows.map((row) => (
          <Link className="sync-row" to={row.target} key={row.key}>
            <div>
              <strong>{row.title}</strong>
              <span>{row.body}</span>
            </div>
            <StatusBadge tone={row.tone}>{row.value}</StatusBadge>
          </Link>
        ))}
      </div>
    </section>
  );
}

function activityRow(log: AnyRecord) {
  const metadata = logMetadata(log);
  return {
    key: String(log.id || `${log.action}:${log.createdAt}`),
    createdAt: log.createdAt,
    actor: String(log.actorLabel || log.actor || log.userLabel || "System"),
    summary: activitySummary(log, metadata)
  };
}

function activitySummary(log: AnyRecord, metadata: AnyRecord): string {
  const action = String(log.action || "");
  const resourceId = String(log.resourceId || "");
  if (action === "cafe24.orders.sync") {
    const orders = Number(metadata.orders || metadata.orderCount || metadata.totalOrderCount || 0);
    const reservations = Number(metadata.reservationSummary?.reserved || metadata.reservedCount || 0);
    const suffix = reservations ? ` · 예약 ${formatNumber(reservations)}건 반영` : "";
    return orders ? `Cafe24 주문 ${formatNumber(orders)}건 읽기 완료${suffix}` : "Cafe24 주문 상태 읽기 완료";
  }
  if (action === "cafe24.orders.sync_failed") return "Cafe24 주문 읽기 실패 · 연결과 권한 확인 필요";
  if (action === "cafe24.products.sync") return "Cafe24 상품 정보를 백오피스로 읽었습니다.";
  if (action === "cafe24.products.sync_failed") return "Cafe24 상품 읽기 실패 · 연결과 권한 확인 필요";
  if (action === "settings.account.create") return `계정 추가 완료${resourceId ? ` · 사용자 ${resourceId}` : ""}`;
  if (action === "settings.account.update") return `계정 정보 변경${resourceId ? ` · 사용자 ${resourceId}` : ""}`;
  if (action === "settings.location.save") return `재고 위치 설정 변경${resourceId ? ` · ${resourceId}` : ""}`;
  if (action === "integrations.online_mall.save") return "온라인몰 API 설정 저장";
  if (action === "cafe24.oauth.connected") return "Cafe24 인증 연결 완료";
  if (action === "cafe24.oauth.disconnect") return "Cafe24 연결 해제";
  if (action === "inventory.receive") return "입고 수량 반영";
  if (action === "inventory.move") return "재고 위치 이동";
  if (action === "inventory.channel_allocation.save") return "온라인몰 할당 기준 저장";
  if (action === "products.price.update") return "상품 판매가 변경";
  if (action === "products.product.update") return "상품 정보 변경";
  if (action === "products.product.delete") return "상품 삭제";
  return defaultActivitySummary(log);
}

function defaultActivitySummary(log: AnyRecord): string {
  const action = String(log.action || "활동 기록");
  const resource = [log.resourceType, log.resourceId].map((value) => String(value || "")).filter(Boolean).join(" · ");
  return resource ? `${actionLabel(action)} · ${resource}` : actionLabel(action);
}

function actionLabel(action: string): string {
  return action
    .split(".")
    .filter(Boolean)
    .map((part) => ({
      cafe24: "Cafe24",
      settings: "설정",
      inventory: "재고",
      products: "상품",
      orders: "주문",
      sync: "동기화",
      save: "저장",
      update: "변경",
      create: "생성",
      delete: "삭제"
    })[part] || part)
    .join(" ");
}

function logMetadata(log: AnyRecord): AnyRecord {
  if (log.metadata && typeof log.metadata === "object" && !Array.isArray(log.metadata)) return log.metadata;
  if (typeof log.metadata === "string") {
    try {
      const parsed = JSON.parse(log.metadata);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function onlineMallProviders(data: AppData): AnyRecord[] {
  const api = data.onlineMallApis as AnyRecord | undefined;
  return Array.isArray(api?.providers) ? api.providers : [];
}

function isProviderConnected(provider: AnyRecord): boolean {
  return provider.connected === true || ["connected", "configured", "active"].includes(String(provider.status || ""));
}

function WorkQueue({ title, body, items }: { title: string; body: string; items: WorkItem[] }) {
  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2>{title}</h2>
          <p>{body}</p>
        </div>
      </div>
      <table className="queue-table">
        <thead>
          <tr>
            <th>작업</th>
            <th>건수</th>
            <th>우선순위</th>
            <th>작업</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.key}>
              <td>
                <strong>{item.title}</strong>
                <span>{item.body}</span>
              </td>
              <td className="num">{formatNumber(item.count)}</td>
              <td><PriorityBadge priority={item.priority} /></td>
              <td><Link className="text-link" to={item.view ? `${item.target}?view=${item.view}` : item.target}>열기</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PriorityBadge({ priority }: { priority: WorkItem["priority"] }) {
  const tone = priority === "긴급" ? "danger" : priority === "높음" ? "warning" : priority === "주의" ? "info" : "default";
  return <StatusBadge tone={tone}>{priority}</StatusBadge>;
}
